import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Users } from 'lucide-react';
import MainContainer from '@/components/containers/MainContainer';
import LeftPanel from '@/components/LeftPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { SearchContext } from '../../project/building/Building';
import { useManufacturerDropdown } from '../manufacturer/hooks/useManufacturer';
import ConsultantDetail from './components/ConsultantDetail';
import ConsultantModal from './components/modals/ConsultantModal';
import {
  useConsultantList,
  useConsultantDetail,
  useToggleConsultantActive,
} from './hooks/useConsultant';

function Consultant() {
  const permission = useSelector((state: any) => state.auth.pagePermission);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<{ is_active?: string[] }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const { data: list = [], isLoading } = useConsultantList(search);
  const { data: detail } = useConsultantDetail(selectedId);
  const { data: manufacturers = [], isLoading: manufacturersLoading } = useManufacturerDropdown();
  const toggleActive = useToggleConsultantActive();

  const manufacturerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    manufacturers.forEach((m) => {
      map[m.id] = m.title;
    });
    return map;
  }, [manufacturers]);

  const items = useMemo(() => {
    const active = filter.is_active;
    const withTitle = list.map((item) => ({
      ...item,
      title: `${item.first_name} ${item.last_name}`.trim(),
    }));
    if (!active || active.length === 0) return withTitle;
    return withTitle.filter((item) => active.map(String).includes(String(item.is_active)));
  }, [list, filter]);

  useEffect(() => {
    if (!selectedId && items.length > 0) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  const handleToggleActive = () => {
    if (!detail) return;
    toggleActive.mutate({ id: detail.id, isActive: detail.is_active });
  };

  return (
    <SearchContext.Provider value={{ search, filter, setSearch, setFilter }}>
      <MainContainer>
        <LeftPanel
          title="Konzultanti"
          data={items}
          loading={isLoading}
          makeActive={setSelectedId}
          active={selectedId}
          addItem={() => {
            setIsEdit(false);
            setOpenDialog(true);
          }}
          onSearch={(e: any) => setSearch(e?.target?.value || '')}
          permission={permission}
          filteredValues={{
            is_active: [
              { value: 'true', title: 'Aktivní' },
              { value: 'false', title: 'Neaktivní' },
            ],
          }}
        />

        {detail ? (
          <ConsultantDetail
            data={detail}
            manufacturerName={manufacturerNameById[detail.manufacturer]}
            onEdit={() => {
              setIsEdit(true);
              setOpenDialog(true);
            }}
            onToggleActive={handleToggleActive}
          />
        ) : isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <SpinnerOverlay />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState icon={<Users />} title="Vyberte konzultanta" />
          </div>
        )}

        <ConsultantModal
          title="Konzultant"
          visible={openDialog}
          formProps={isEdit ? detail : null}
          manufacturers={manufacturers}
          manufacturersLoading={manufacturersLoading}
          onSaved={setSelectedId}
          onClose={() => {
            setIsEdit(false);
            setOpenDialog(false);
          }}
        />
      </MainContainer>
    </SearchContext.Provider>
  );
}

export default Consultant;
